import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Select,
  MenuItem,
  TextField,
  Box,
  styled,
  Typography,
  IconButton,
} from "@mui/material";
import dayjs from "dayjs";
import styles from "../../styles/common/brsrTable.module.css";
import { Add, Delete } from "@mui/icons-material";
import DeleteRowDialog from "./DeleteRowDialog";

const DynamicTable = ({
  data,
  columns,
  onRowChange,
  onAddRow,
  defaultRow,
  onDeleteRow,
  setRows,
  errors,
  confirmRemoveRow,
  cancelRemoveRow,
  openDialog,
  disabled,
  fieldDisabled,
  hide,
}) => {
  const today = dayjs();
  const [isFilleds, setIsFilled] = useState({});
  const [fieldToRemove, setFieldToRemove] = React.useState({
    tableIndex: null,
    rowIndex: null,
  });
  const CustomTextField = styled(TextField)(({ theme }) => ({
    "& .MuiInputBase-root": {
      height: "30px", // Set height of input field
      MAX_CALENDAR_HEIGHT: "30px",
      border: "none",
    },
    "& .MuiSelect-select": {
      height: "40px", // Ensure select dropdown also has 40px height
      display: "flex",
      alignItems: "center",
      border: "none",
    },
  }));

  const formatFieldName = (field) => {
    return field
      .replace(/([A-Z])/g, " $1") // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim();
  };

  useEffect(() => {
    checkIfAdded();
    setRows && setRows(data.length);
  }, [data, columns]);

  const flattenObject = (obj, prefix = "") => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (typeof value === "object" && value !== null) {
        Object.assign(acc, flattenObject(value, newKey)); // Recursively flatten nested objects
      } else {
        acc[newKey] = value;
      }
      return acc;
    }, {});
  };

  const isFilled = (value) => {
    return (
      value !== undefined &&
      value !== "" &&
      value !== null &&
      !(Array.isArray(value) && value.length === 0)
    );
  };

  const checkIfAdded = () => {
    const rows = data || [];
    const lastRow = rows.length > 0 ? rows[rows.length - 1] : {};

    // Determine which fields from columns are inputs (exclude static columns)
    const columnFields = (columns || [])
      .filter((col) => col && col.field && col.type !== "static")
      .map((col) => col.field);

    // Special handling: if the table has both 'approval' and 'reason'
    // - allow Add when approval === true even if reason is empty
    // - disallow Add when approval === false and reason is empty
    let isComplete = false;

    if (columnFields.includes("approval") && columnFields.includes("reason")) {
      // All fields except 'reason' must be filled
      const nonReasonFields = columnFields.filter((f) => f !== "reason");
      const nonReasonFilled = nonReasonFields.every((field) => isFilled(lastRow[field]));

      if (!nonReasonFilled) {
        isComplete = false;
      } else {
        // Approval selected and other fields filled
        if (lastRow.approval === true) {
          // Reason optional when approved
          isComplete = true;
        } else if (lastRow.approval === false) {
          // Reason required when not approved
          isComplete = isFilled(lastRow.reason);
        } else {
          // Approval not selected yet
          isComplete = false;
        }
      }
    } else {
      // Generic completeness check for tables without approval/reason behavior
      isComplete = columnFields.every((field) => isFilled(lastRow[field]));
    }

    // Disable Add when last row is incomplete
    setIsFilled(!isComplete);
  };

  return (
    <>
      <DeleteRowDialog {...{ openDialog, cancelRemoveRow, confirmRemoveRow }} />
      <TableContainer
        component={Paper}
        sx={{ borderRadius: "10px" }}
        className={styles["dynamic-table-container"]}
      >
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col, index) => (
                <TableCell
                  key={index}
                  align="left"
                  sx={{ border: "1px solid #ddd" }}
                  className={styles["dynamic-table-header-cell"]}
                >
                  {col.label}
                </TableCell>
              ))}
              <TableCell
                sx={{ border: "1px solid #ddd" }}
                className={styles["dynamic-table-header-cell"]}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
              >
                {/* <TableCell>{rowIndex+1}.</TableCell> */}
                {columns.map((col, colIndex) => (
                  <TableCell
                    key={colIndex}
                    className={styles["dynamic-table-cell"]}
                    sx={{
                      border: "1px solid #DBDBDB",
                      width: col.width,
                      maxWidth: col.width,
                    }}
                  >
                    {/* {col.type === "select" ? ( */}
                    {col.type === "static" ? (
                      // <Typography variant="body2">{row[col.field]}</Typography>
                      <Typography
                        InputProps={{
                          disableUnderline: true,
                          className: styles["dynamic-text-field"],
                        }}
                        className={styles["dynamic-text-field"]}
                      >
                        {rowIndex + 1}.
                      </Typography>
                    ) : col.type === "select" ? (
                      <Select
                        disabled={disabled}
                        value={row[col.field] ?? ""}
                        size="small"
                        onChange={(e) =>
                          onRowChange(
                            rowIndex,
                            col.field,
                            e.target.value,
                            col.type
                          )
                        }
                        fullWidth
                        className={styles["dynamic-select-input"]}
                        sx={{
                          width: "100%",
                          "& fieldset": { border: "none", width: "100%" }, // Removes the border
                        }}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select
                        </MenuItem>
                        {col.options.map((option, optIndex) => (
                          <MenuItem key={optIndex} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : col.type === "date" ? (
                      <input
                        type="date"
                        name={col?.field}
                        disabled={disabled}
                        value={
                          row[col.field]
                            ? dayjs(row[col.field]).format("YYYY-MM-DD")
                            : ""
                        } // Format to YYYY-MM-DD
                        onChange={(newValue) => {
                          if (newValue && dayjs(newValue).isAfter(today)) {
                            onRowChange(
                              rowIndex,
                              col?.field,
                              null,
                              col.type ? col.type : "text"
                            ); // Reset value or handle as needed
                          } else {
                            onRowChange(
                              rowIndex,
                              col?.field,
                              newValue.target.value,

                              "date"
                            );
                          }
                        }}
                        // disabled={disable}
                        max={today}
                        // min={minDate}
                        onFocus={(e) => {
                          e.target.style.border = "2px solid #418eda"; // Blue border
                          e.target.style.paddingLeft = "9px"; // Reduce padding
                          e.target.style.paddingRight = "9px"; // Reduce padding
                        }}
                        onBlur={(e) => {
                          e.target.style.border = "1px solid #B8B8B8"; // Reset border
                          e.target.style.padding = "10px"; // Reset padding
                        }}
                        style={{
                          width: "100%",
                          height: "40px",
                          padding: "10px",
                          backgroundColor: "#EDEDED",
                          border: "1px solid #B8B8B8",
                          borderRadius: "5px",
                          outline: "none",
                          // transition: "all 0.2s ease-in-out", // Smooth transition
                        }}
                      />
                    ) : (
                      <TextField
                        disabled={
                          disabled || (col?.field === "reason" && row?.approval === true)
                        }
                        size="small"
                        fullWidth
                        name={col.field}
                        value={row[col.field]}
                        onChange={(e) =>
                          onRowChange(
                            rowIndex,
                            col.field,
                            e.target.value,
                            col.type ? col.type : "text"
                          )
                        }
                        placeholder="Type Here"
                        multiline
                        // rows={1}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          className: styles["dynamic-text-field"],
                        }}
                        className={styles["dynamic-text-field"]}
                      />
                    )}
                  </TableCell>
                ))}
                <TableCell sx={{ width: "8%" }}>
                  <IconButton
                    // disabled={tables[tableIndex]?.rows?.length === 1}
                    disabled={data?.length === 1 || disabled}
                    // onClick={() => handleRemoveRow(rowIndex)}
                    onClick={() => onDeleteRow(rowIndex)} // Use the onDeleteRow prop
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {errors &&
        errors.length > 0 &&
        errors.map((item, index) => {
          // const [main,sub]=item.split(":");
          const missingFields = Object.values(item).filter((key) => !item[key]); // Find missing fields
          if (missingFields.length > 0) {
            // const [main,sub]=missingFields.split(":");
            return (
              <Typography
                key={index}
                variant="body2"
                color="error"
                style={{
                  lineHeight: "1.2",
                  margin: "2px 0",
                  color: "#d34040",
                  fontSize: "10px",
                  fontStyle: "italic",
                  marginTop: "10px",
                }}
              >
                Row {index + 1}: Missing fields - {missingFields.join(", ")}
              </Typography>
            );
          }
          return null;
        })}

      {!hide && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            disabled={isFilleds || disabled || false}
            startIcon={<Add />}
            onClick={() => onAddRow(defaultRow)}
            sx={{
              color: "#1C486B",
              width: "7rem",
              borderColor: "#1C486B",
            }}
            variant="outlined"
          >
            Add
          </Button>
        </Box>
      )}
    </>
  );
};

export default DynamicTable;
